/**
 * First-party telemetry beacons (issue #79).
 *
 * Fire-and-forget POSTs to our own backend — no third party, no personal data.
 * The backend scrubs error text (emails / long numbers / query strings) and only
 * accepts allowlisted event names. Every failure is swallowed: telemetry must
 * never break or slow the app.
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Throttle identical errors and cap volume so a render loop can't flood us.
const DEDUPE_MS = 60_000;
const MAX_ERRORS_PER_SESSION = 50;
const recentErrors = new Map(); // "name:message" -> last-sent ms
let errorsSent = 0;

// The untouched fetch, captured before installGlobalErrorReporting wraps it, so
// beacons are never themselves recorded as network failures.
const rawFetch = typeof window !== 'undefined' && window.fetch ? window.fetch.bind(window) : null;

function post(path, body) {
  try {
    // keepalive lets the beacon survive a page unload/navigation.
    rawFetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch (_) {
    /* ignore — never throw from telemetry */
  }
}

export function reportError({ name, message, url, source = 'web', appVersion } = {}) {
  if (errorsSent >= MAX_ERRORS_PER_SESSION) return;
  const key = `${name || ''}:${message || ''}`;
  const now = Date.now();
  const last = recentErrors.get(key);
  if (last && now - last < DEDUPE_MS) return;
  recentErrors.set(key, now);
  errorsSent += 1;

  post('/telemetry/error', {
    name: name ? String(name).slice(0, 120) : null,
    message: message ? String(message).slice(0, 1000) : null,
    // Path only — the backend also strips host/query, but keep it tidy here too.
    url: url || (typeof window !== 'undefined' ? window.location.pathname : null),
    source,
    app_version: appVersion || null,
  });
}

/** Increment an allowlisted product-signal counter (unknown names are ignored server-side). */
export function reportEvent(name, count = 1) {
  post('/telemetry/event', { name, count });
}

// ---------------------------------------------------------------------------
// Network-level failures (#364)
//
// When a network blocks or can't reach the API, a beacon sent at that moment
// dies with everything else. So failures are queued in localStorage, collapsed
// per (class, path), and delivered the next time the API answers.
// ---------------------------------------------------------------------------

const QUEUE_KEY = 'dynerz.telemetry.networkQueue';
const QUEUE_MAX = 20;
// Gateway errors come from the proxy when the backend is down — the backend's
// own request rollup can never see those. Plain 500s it already records.
const GATEWAY_STATUSES = new Set([502, 503, 504]);

function readQueue() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(QUEUE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function writeQueue(queue) {
  try {
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (_) {
    /* storage full or blocked — drop silently */
  }
}

/** wifi / cellular / wired / unknown. Never an SSID — browsers don't expose it anyway. */
export function currentNetworkType() {
  const type = typeof navigator !== 'undefined' ? navigator.connection?.type : undefined;
  if (type === 'wifi' || type === 'cellular') return type;
  if (type === 'ethernet') return 'wired';
  return 'unknown';
}

/**
 * Classify a failed fetch. Browsers report DNS, TLS and CORS failures all as
 * the same TypeError, so the web can only tell "offline" from "connection";
 * the native apps can be more specific. Returns null for a caller's own abort.
 */
export function classifyFetchFailure({ error, status, online = true } = {}) {
  if (error) {
    if (error.name === 'AbortError') return null;
    return online ? 'connection' : 'offline';
  }
  if (GATEWAY_STATUSES.has(status)) return 'http_5xx';
  return null;
}

/** Fold one failure into the queue: repeats bump a count instead of adding rows. */
export function enqueueFailure(queue, { failureClass, path, message, networkType, at }) {
  const existing = queue.find((e) => e.failure_class === failureClass && e.url === path);
  if (existing) {
    existing.occurrences += 1;
    existing.last_occurred_at = at;
    existing.network_type = networkType;
    return queue;
  }
  queue.push({
    name: 'NetworkFailure',
    message: message ? String(message).slice(0, 300) : null,
    url: path,
    failure_class: failureClass,
    network_type: networkType,
    occurred_at: at,
    last_occurred_at: at,
    occurrences: 1,
  });
  // Keep the newest; a long outage on one path is still one row.
  return queue.slice(-QUEUE_MAX);
}

function recordNetworkFailure(failureClass, path, message) {
  writeQueue(enqueueFailure(readQueue(), {
    failureClass,
    path,
    message,
    networkType: currentNetworkType(),
    at: new Date().toISOString(),
  }));
}

let flushing = false;

/** Deliver queued failures; an entry leaves the queue only once the API accepts it. */
export async function flushNetworkQueue() {
  if (flushing || !rawFetch) return;
  const pending = readQueue();
  if (pending.length === 0) return;
  flushing = true;
  try {
    const delivered = new Set();
    for (const entry of pending) {
      try {
        const res = await rawFetch(`${API_BASE_URL}/telemetry/error`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...entry, source: 'web' }),
        });
        if (!res.ok) break;
        delivered.add(entry);
      } catch (_) {
        break; // still unreachable — try again later
      }
    }
    if (delivered.size > 0) {
      // Re-read: failures may have been queued while we were sending.
      const sent = (e) => [...delivered].some(
        (d) => d.failure_class === e.failure_class && d.url === e.url
          && d.last_occurred_at === e.last_occurred_at,
      );
      writeQueue(readQueue().filter((e) => !sent(e)));
    }
  } finally {
    flushing = false;
  }
}

function apiPath(input) {
  const url = typeof input === 'string' ? input : input?.url;
  if (!url || !url.startsWith(API_BASE_URL)) return null;
  const path = url.slice(API_BASE_URL.length).split(/[?#]/)[0] || '/';
  return path.startsWith('/telemetry') ? null : path;
}

function installNetworkFailureReporting() {
  if (!rawFetch || window.fetch.__dynerzTelemetry) return;
  const wrapped = async (input, init) => {
    const path = apiPath(input);
    if (!path) return rawFetch(input, init);
    let res;
    try {
      res = await rawFetch(input, init);
    } catch (error) {
      const failureClass = classifyFetchFailure({ error, online: navigator.onLine !== false });
      if (failureClass) recordNetworkFailure(failureClass, path, error?.message);
      throw error;
    }
    const failureClass = classifyFetchFailure({ status: res.status });
    if (failureClass) {
      recordNetworkFailure(failureClass, path, `HTTP ${res.status}`);
    } else {
      // The API is answering again — deliver anything queued while it wasn't.
      flushNetworkQueue();
    }
    return res;
  };
  wrapped.__dynerzTelemetry = true;
  window.fetch = wrapped;
  window.addEventListener('online', () => flushNetworkQueue());
  flushNetworkQueue();
}

/** Register global handlers for uncaught errors + unhandled promise rejections. */
export function installGlobalErrorReporting() {
  if (typeof window === 'undefined') return;

  installNetworkFailureReporting();

  window.addEventListener('error', (event) => {
    reportError({
      name: event.error?.name || 'Error',
      message: event.message || String(event.error || 'unknown error'),
      url: window.location.pathname,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportError({
      name: reason?.name || 'UnhandledRejection',
      message: reason?.message || String(reason),
      url: window.location.pathname,
    });
  });
}
